import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import { InterpolateIcon } from '~/assets/workspace-icons';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import { useSetOperationPanelUnlessBusy } from './useSetOperationPanelUnlessBusy';
import OperationToolbarButton from './OperationToolbarButton';

// Only interpolate lives here: clearing contours belongs to the eraser
// and to the deletions offered on the structure itself. Interpolate
// creates contours rather than cleaning them, and keeps its own preview
// machinery. Its button opens the interpolation operation.
export default function SliceOperationsSection() {
    // Interpolate modifies the selected ROI's contours in place.
    const {
        selectedRoiContourEditDisabled: isDisabled,
        selectionEditBlocker,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const intl = useIntl();
    const [panelState, setPanelState] = useRecoilState(operationPanelState);
    const setPanelStateUnlessBusy = useSetOperationPanelUnlessBusy();
    const isOpen = panelState.type === 'interpolation';

    // Disabling the button doesn't close an already-open operation.
    useEffect(() => {
        if (isDisabled && isOpen) {
            setPanelState({ type: 'closed' });
        }
    }, [isDisabled, isOpen, setPanelState]);

    const handleToggle = useCallback(() => {
        setPanelStateUnlessBusy((prev) =>
            prev.type === 'interpolation'
                ? { type: 'closed' }
                : { type: 'interpolation' }
        );
    }, [setPanelStateUnlessBusy]);

    const blockedReason = isPrimaryImageIncomplete
        ? imageIncompleteReason
        : selectionEditBlocker
          ? intl.formatMessage({
                id: editBlockedReasonId(selectionEditBlocker),
            })
          : undefined;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
            }}
        >
            <OperationToolbarButton
                icon={<InterpolateIcon />}
                toolName={intl.formatMessage({
                    id: 'toolbar.interpolation.tooltip',
                })}
                disabledReason={blockedReason}
                selected={isOpen}
                disabled={isDisabled}
                onClick={handleToggle}
                dockAnchor="interpolation"
            />
        </Box>
    );
}
